/**
 * O PLANEJAMENTO DO QUADRINHO, NA TELA — T19 (11/10/2026).
 *
 * "Tem que ter uma inteligência de entender exatamente dentro do que eu pedi da história, se
 *  montar um planejamento, um artefato planejando como vai ser estruturada essa história e de
 *  que forma a gente vai botar isso num canvas" — e "a partir disso vai gerar".
 *
 * Esta é a etapa entre escrever e desenhar: a pessoa vê, tira por tira, o que cada quadro vai
 * mostrar e que texto ele leva — e só então manda montar o canvas e desenhar.
 */
import { PlanoDeQuadrinho, Recurso } from "../../api/roteiro";

const ROTULO: Record<Recurso["tipo"], string> = {
  recordatorio: "Recordatório (o quadrado que conta o que aconteceu)",
  fala: "Balão de fala",
  pensamento: "Balão de pensamento",
};

interface Props {
  plano: PlanoDeQuadrinho;
  /** T29.7 · a tirinha fala em tiras lado a lado; a página de revista, em páginas. */
  formato?: string;
  aoDesenhar: () => void;
  aoMudar: () => void;
  ocupado: boolean;
}

export default function PlanoDoQuadrinho({ plano, formato = "tirinha", aoDesenhar, aoMudar, ocupado }: Props) {
  const tiras = Array.from({ length: plano.tiras }, (_, i) => plano.quadros.filter((q) => q.tira === i + 1));
  // T29.7 · «"tiras lado a lado" na A4» (relatório de 14/10): o resumo era o mesmo em todo formato.
  const tirinha = formato === "tirinha";
  const rotulo = tirinha ? "Tira" : "Página";
  return (
    <section className="plano-do-quadrinho" data-testid="plano-do-quadrinho" aria-label="Planejamento do quadrinho">
      <h2>O planejamento da sua história</h2>
      <p className="plano-resumo">
        {tirinha
          ? `${plano.tiras === 1 ? "1 tira" : `${plano.tiras} tiras`} de ${plano.quadrosPorTira} quadros lado a lado`
          : `${plano.tiras === 1 ? "1 página" : `${plano.tiras} páginas`}, até ${plano.quadrosPorTira} quadros por página`}
        {" — "}
        {plano.quadros.length} quadros no total. {plano.porque}.
      </p>
      {tiras.map((quadros, t) => (
        <div key={t} className="plano-tira" data-testid="plano-tira">
          <h3>{rotulo} {t + 1}</h3>
          <ol className="plano-quadros">
            {quadros.map((q) => (
              <li key={q.numero} className={`plano-quadro ${q.pausa ? "pausa" : ""}`} data-testid="plano-quadro">
                <p className="plano-quadro-topo">
                  <strong>Quadro {q.numero}</strong> · {q.papel}
                </p>
                <p><span className="plano-rotulo">O que acontece:</span> {q.acontece}</p>
                <p><span className="plano-rotulo">Quem aparece:</span> {q.quem.length ? q.quem.join(", ") : "—"}</p>
                <p><span className="plano-rotulo">Enquadramento:</span> {q.enquadramento}</p>
                {q.recursos.length === 0 ? (
                  <p className="plano-recurso nenhum">
                    {q.numero === plano.quadros.length
                      ? "Só desenho, sem texto — o desfecho, em silêncio."
                      : "Só desenho, sem texto — uma pausa antes do desfecho."}
                  </p>
                ) : (
                  <ul className="plano-recursos">
                    {q.recursos.map((r, k) => (
                      <li key={k} className={`plano-recurso ${r.tipo}`}>
                        <span className="plano-rotulo">{ROTULO[r.tipo]}{r.quem ? ` — ${r.quem}` : ""}:</span> “{r.texto}”
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </div>
      ))}
      <div className="plano-acoes">
        <button type="button" className="roteirizar-botao" onClick={aoDesenhar} disabled={ocupado}>
          Montar o quadrinho e desenhar
        </button>
        <button type="button" className="roteirizar-botao-secundario" onClick={aoMudar} disabled={ocupado}>
          Mudar a história
        </button>
      </div>
    </section>
  );
}
